import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { AppShell } from './app/AppShell';
import { LoadingSkeleton } from './app/LoadingSkeleton';
import { useUIStore } from './stores';
import { initDesktopServices } from './lib/db/init';
import './styles/tokens.css';
import './styles/components.css';

// Initialize desktop services (SQLite + connectivity) if running in Tauri.
// This is fire-and-forget — the app shell renders immediately.
initDesktopServices();

// Lazy-load heavy views for code splitting
const WarmupView = lazy(() => import('./game/WarmupView').then(m => ({ default: m.WarmupView })));
const LanesView = lazy(() => import('./game/LanesView').then(m => ({ default: m.LanesView })));
const GameView = lazy(() => import('./game/GameView').then(m => ({ default: m.GameView })));
const ProfileView = lazy(() => import('./app/ProfileView').then(m => ({ default: m.ProfileView })));
const LeaderboardView = lazy(() => import('./leaderboard').then(m => ({ default: m.LeaderboardView })));
const ProUpgrade = lazy(() => import('./app/ProUpgrade').then(m => ({ default: m.ProUpgrade })));
const TeamView = lazy(() => import('./app/TeamView').then(m => ({ default: m.TeamView })));
const SkillReport = lazy(() => import('./app/SkillReport').then(m => ({ default: m.SkillReport })));
const TeamLeaderboard = lazy(() => import('./app/TeamLeaderboard').then(m => ({ default: m.TeamLeaderboard })));
const AnalyticsDashboard = lazy(() => import('./app/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const TeamAdminDashboard = lazy(() => import('./app/TeamAdminDashboard').then(m => ({ default: m.TeamAdminDashboard })));
const TournamentLobby = lazy(() => import('./tournament/TournamentLobby').then(m => ({ default: m.TournamentLobby })));

function MainContent() {
  const currentView = useUIStore((state) => state.currentView);
  
  return (
    <Suspense fallback={<LoadingSkeleton />}>
      {currentView === 'warmup' && (
        <div className="view-content view-content--full">
          <WarmupView />
        </div>
      )}
      
      {currentView === 'lanes' && (
        <div className="view-content view-content--full">
          <LanesView />
        </div>
      )}
      
      {currentView === 'challenges' && (
        <div className="view-content view-content--full">
          <GameView />
        </div>
      )}
      
      {currentView === 'profile' && (
        <div className="view-content view-content--full">
          <ProfileView />
        </div>
      )}
      
      {currentView === 'leaderboard' && (
        <div className="view-content">
          <LeaderboardView />
        </div>
      )}

      {currentView === 'pro' && (
        <div className="view-content">
          <ProUpgrade />
        </div>
      )}

      {currentView === 'team' && (
        <div className="view-content">
          <TeamView />
        </div>
      )}

      {currentView === 'report' && (
        <div className="view-content">
          <SkillReport />
        </div>
      )}

      {currentView === 'team-leaderboard' && (
        <div className="view-content">
          <TeamLeaderboard />
        </div>
      )}

      {currentView === 'analytics' && (
        <div className="view-content">
          <AnalyticsDashboard />
        </div>
      )}

      {currentView === 'team-admin' && (
        <div className="view-content">
          <TeamAdminDashboard />
        </div>
      )}

      {currentView === 'tournaments' && (
        <div className="view-content">
          <TournamentLobby />
        </div>
      )}

      {!['warmup', 'lanes', 'challenges', 'profile', 'leaderboard', 'pro', 'team', 'report', 'team-leaderboard', 'analytics', 'team-admin', 'tournaments'].includes(currentView) && (
        <div className="view-content">
          <h2 className="view-fallback-title">
            {currentView}
          </h2>
        </div>
      )}
    </Suspense>
  );
}

function App() {
  return (
    <AppShell>
      <MainContent />
    </AppShell>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
